/**
 * ErrorState — shown when a query fails or a feature is disabled.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.1.2 (Phase 1 foundation).
 * Used in two flavors:
 *   - Hard failure (network / 5xx) — caller passes `onRetry`.
 *   - Feature disabled (503 / `PortalFeatureDisabledError`) — caller
 *     omits `onRetry` and passes an explanatory `description`.
 */
import type { ReactNode } from 'react';
import { Button } from './Button';

interface ErrorStateProps {
  title?: string;
  description?: ReactNode;
  /** When provided, renders a "Try again" button. Omit for non-retryable
   *  conditions (e.g. feature disabled). */
  onRetry?: () => void;
  /** Override the retry-button label. */
  retryLabel?: string;
}

export function ErrorState({
  title = 'Something went wrong.',
  description,
  onRetry,
  retryLabel = 'Try again',
}: ErrorStateProps): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '2rem 1.5rem',
        textAlign: 'center',
        color: '#7f1d1d',
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '0.5rem',
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>{title}</p>
      {description ? (
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b' }}>{description}</p>
      ) : null}
      {onRetry ? (
        <div style={{ marginTop: '0.5rem' }}>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
